import type { ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Switch } from '../Switch';
import { FormField, StaticValue } from './FormField';
import { type BaseFieldProps, describedBy, useBoundField, useFieldIds } from './fieldBase';

// ——— toggle / static ———

export interface ToggleFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  onLabel?: string;
  offLabel?: string;
}

/** Toggle (README): контейнер 34 / radius 16 / рамка line-field, тумблер и подпись 13 ink-700. */
export function ToggleField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide,
  disabled,
  onLabel,
  offLabel,
}: ToggleFieldProps<TValues>) {
  const { t } = useTranslation('form');
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const checked = field.value === true;
  const error = fieldState.error?.message;
  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <div className="flex h-34 items-center gap-10 rounded-16 border border-line-field bg-paper-pure px-10">
        <Switch
          id={ids.id}
          ref={field.ref}
          checked={checked}
          onCheckedChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled || field.disabled}
          aria-describedby={describedBy(Boolean(error), Boolean(hint), ids)}
        />
        <span className="text-[13px] text-ink-700">
          {checked ? (onLabel ?? t('toggle.on')) : (offLabel ?? t('toggle.off'))}
        </span>
      </div>
    </FormField>
  );
}

export interface StaticFieldProps {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  wide?: boolean;
}

/** Static — значение только для чтения (системное или вычисляемое поле). */
export function StaticField({ label, value, hint, wide }: StaticFieldProps) {
  const ids = useFieldIds();
  return (
    <FormField label={label} labelId={ids.labelId} hint={hint} hintId={ids.hintId} wide={wide}>
      <StaticValue id={ids.id}>{value}</StaticValue>
    </FormField>
  );
}
